import React, { useState, useEffect, useCallback } from 'react';
import type { AppEvent } from '@daddytime/events';
import Layout from '../components/Layout';
import EventFilters, { EventFiltersState } from '../components/EventFilters';
import EventsList from '../components/EventsList';
import EventDetail from '../components/EventDetail';
import { queryEvents } from '../lib/api';
import { RefreshCw, ChevronDown } from 'lucide-react';

export default function EventsPage() {
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<EventFiltersState>({});
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<AppEvent | null>(null);

  const fetchEvents = useCallback(async (newCursor?: string, append = false) => {
    setLoading(true);
    setError(null);

    try {
      const result = await queryEvents({
        event_type: filters.eventType,
        user_id: filters.userId,
        start_date: filters.startDate,
        end_date: filters.endDate,
        is_test: filters.isTest,
        cursor: newCursor,
        limit: 50,
      });

      if (append) {
        setEvents(prev => [...prev, ...result.events]);
      } else {
        setEvents(result.events);
      }
      setCursor(result.cursor);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Initial load
  useEffect(() => {
    fetchEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyFilters = () => {
    setCursor(undefined);
    fetchEvents();
  };

  const handleLoadMore = () => {
    if (cursor) {
      fetchEvents(cursor, true);
    }
  };

  const handleRefresh = () => {
    setCursor(undefined);
    fetchEvents();
  };

  return (
    <Layout>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Events</h2>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <EventFilters
        filters={filters}
        onChange={setFilters}
        onApply={handleApplyFilters}
        loading={loading}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <EventsList
        events={events}
        onSelect={setSelectedEvent}
        loading={loading}
      />

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <ChevronDown className="w-5 h-5" />
            Load More
          </button>
        </div>
      )}

      {events.length > 0 && (
        <p className="mt-4 text-sm text-gray-500 text-center">
          Showing {events.length} events
        </p>
      )}

      {selectedEvent && (
        <EventDetail
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </Layout>
  );
}
